import React, { createContext, useContext, useState, useEffect } from "react";
import { fetchAuthSession } from "aws-amplify/auth";
import {
  signIn as cognitoSignIn,
  signUp as cognitoSignUp,
  signOut as cognitoSignOut,
  getCurrentUser,
  fetchUserAttributes,
  confirmSignUp as cognitoConfirmSignUp,
} from "aws-amplify/auth";
import type { User, Organization, AuthState } from "../types";

interface AuthContextType extends AuthState {
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (data: SignUpData) => Promise<{ needsConfirmation: boolean }>;
  confirmSignUp: (email: string, code: string) => Promise<void>;
  signOut: () => Promise<void>;
}

export interface SignUpData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  organizationName: string;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

function generateId(): string {
  return crypto.randomUUID();
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    organization: null,
    isAuthenticated: false,
    isLoading: true,
  });

  useEffect(() => {
    checkAuth();
  }, []);

  async function checkAuth() {
    try {
      const currentUser = await getCurrentUser();
      const session = await fetchAuthSession();
      if (!currentUser) throw new Error("No user");

      const attributes = await fetchUserAttributes();

      const user: User = {
        id: attributes.sub || "",
        email: attributes.email || "",
        firstName: attributes.given_name || "",
        lastName: attributes.family_name || "",
        role: (attributes["custom:role"] as User["role"]) || "admin",
        organizationId: attributes["custom:organizationId"] || "",
        createdAt: "",
      };

      const organization: Organization = {
        id: attributes["custom:organizationId"] || "",
        name: attributes["custom:organizationName"] || "",
        slug: (attributes["custom:organizationName"] || "")
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "-"),
        plan: (() => { const groups = (session.tokens?.idToken?.payload?.["cognito:groups"] as string[]) || []; const tiers = ["enterprise", "professional", "starter", "free"]; return (tiers.find((t) => groups.includes(t)) || "free") as any; })(),
        createdAt: "",
      };

      setState({
        user,
        organization,
        isAuthenticated: true,
        isLoading: false,
      });
    } catch {
      // No valid session — clear any stale data
      try { await cognitoSignOut(); } catch { /* ignore */ }
      setState({
        user: null,
        organization: null,
        isAuthenticated: false,
        isLoading: false,
      });
    }
  }

  const signIn = async (email: string, password: string) => {
    // Always clear any existing session first
    try { await cognitoSignOut(); } catch { /* ignore */ }

    const result = await cognitoSignIn({ username: email, password });

    if (result.isSignedIn) {
      await checkAuth();
    } else {
      throw new Error("Sign in incomplete. Please check your credentials.");
    }
  };

  const signUp = async (
    data: SignUpData
  ): Promise<{ needsConfirmation: boolean }> => {
    const orgId = generateId();

    // Clear any existing session before sign-up
    try { await cognitoSignOut(); } catch { /* ignore */ }

    const result = await cognitoSignUp({
      username: data.email,
      password: data.password,
      options: {
        userAttributes: {
          email: data.email,
          given_name: data.firstName,
          family_name: data.lastName,
          "custom:organizationId": orgId,
          "custom:organizationName": data.organizationName,
          "custom:role": "admin",
        },
      },
    });

    const needsConfirmation = !result.isSignUpComplete;
    return { needsConfirmation };
  };

  const confirmSignUp = async (email: string, code: string) => {
    await cognitoConfirmSignUp({ username: email, confirmationCode: code });
  };

  const signOut = async () => {
    await cognitoSignOut({ global: true });
    setState({
      user: null,
      organization: null,
      isAuthenticated: false,
      isLoading: false,
    });
  };

  return (
    <AuthContext.Provider
      value={{ ...state, signIn, signUp, confirmSignUp, signOut }}
    >
      {children}
    </AuthContext.Provider>
  );
}
